import {
    getDomain,
    gtmAddPdpProductToCart,
    gtmAdpageAddToCart,
    gtmAdpageItemDetail,
    gtmAdpageItemSizeDetail,
    gtmPdpProductData,
    typedBoolean,
} from "@jog/shared"
import { PdpGroup, PdpProduct, TweakwiseItem } from "@jog/types"
import useEmblaCarousel from "embla-carousel-react"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { kebabCase } from "lodash-es"
import { CSSProperties, useCallback, useEffect, useMemo } from "react"

import { useAddedProduct } from "./useAddedProduct"
import { usePdp } from "./useStaticPdp"

const sortByQuality = ["500", "1000", "2000", "mini"]
export const getItemData = (item: TweakwiseItem, coverType: string): PdpGroup => {
    const name = item.title
    const categories = item.attributes?.find((val) => val.name === "categoryAsUrl" && val.values)?.values?.[0]
    const slug = item.attributes?.find((val) => val.name === "slug" && val.values)?.values?.[0]
    const imageUrl =
        (item.attributes?.find((attribute) => attribute.name === "gallery")?.values || [])
            .map((item) => {
                try {
                    return JSON.parse(item) as { url: string; label: string }
                } catch {
                    return
                }
            })
            .filter(typedBoolean)
            .filter((item) => item.label.includes(coverType))
            .map((i) => {
                const quality = sortByQuality.indexOf(i.label.split("-").at(-1) || "")
                return { ...i, quality }
            })
            .filter(({ quality }) => quality > -1)
            .sort((a, b) => a.quality - b.quality)
            .at(0)?.url || item.image

    const color = item.attributes?.find((val) => val.name === "color" && val.values)?.values?.[0]
    return {
        imageUrl,
        url: `/${categories}/${kebabCase(name)}/${kebabCase(slug)}`,
        title: item.title,
        itemno: item.itemno,
        color,
    }
}

export const usePdpAdded = () => {
    const { detail, productData, sku } = usePdp()
    const { addedProduct } = useAddedProduct()
    return useCallback(() => {
        if (!productData) return
        gtmAddPdpProductToCart(productData, detail.productCategoryPath)
        addedProduct(sku)
        gtmAdpageAddToCart(productData, sku, detail.gtmId, detail.productCategoryPath)
    }, [addedProduct, detail.gtmId, detail.productCategoryPath, productData, sku])
}
export const usePdpGtm = () => {
    const { productData, sku, detail } = usePdp()
    useEffect(() => {
        if (sku && productData) gtmAdpageItemSizeDetail(productData, detail.gtmId, detail.productCategoryPath, sku)
    }, [detail, productData, sku])
    useEffect(() => {
        if (productData) {
            gtmPdpProductData(productData, detail.gtmId, detail.productCategoryPath)
            gtmAdpageItemDetail(productData, detail.gtmId, detail.productCategoryPath)
        }
    }, [detail, productData])
}
export const usePdpStructuredData = () => {
    const { detail, productData, sku } = usePdp()
    return useMemo(() => {
        if (!productData) return null
        const { name, description, desktopImages, color, size, material } = detail
        const domain = getDomain()
        const product = {
            "@context": "https://schema.org",
            "@type": "Product",
            "@id": `${domain}/#Product`,
            name,
            image: desktopImages.map((val) => val?.url),
            offers: {
                "@type": "Offer",
                price: productData.price?.centAmount / 100 || 0,
                priceCurrency: productData.price?.currencyCode || "",
                availability: "https://schema.org/InStock",
                itemCondition: "https://schema.org/NewCondition",
            },
            description,
            color,
            brand: productData.brand,
            sku,
            size,
            material,
        }
        return JSON.stringify(product)
    }, [detail, productData, sku])
}

export const getPdpPrice = ({
    firstPrice,
    price,
    discountedPrice,
    lowestPrice,
}: Pick<PdpProduct, "firstPrice" | "price" | "discountedPrice" | "lowestPrice">) => {
    const originPrice = firstPrice || price
    if (discountedPrice && discountedPrice.centAmount < originPrice.centAmount) {
        const currentPrice = discountedPrice.centAmount
        const _originPrice = originPrice.centAmount
        return {
            currentPrice: currentPrice,
            originPrice: _originPrice,
            lowestPrice,
            off: Math.floor(((_originPrice - currentPrice) / _originPrice) * 100),
        }
    }
    const currentPrice = price.centAmount
    const _originPrice = firstPrice?.centAmount || price.centAmount
    return {
        currentPrice,
        originPrice: _originPrice,
        lowestPrice,
        off: Math.floor(((_originPrice - currentPrice) / _originPrice) * 100),
    }
}
export const usePdpImagesMobile = () => useEmblaCarousel({ axis: "x", align: "start", loop: true })
export const usePdpImages = (length: number) =>
    useEmblaCarousel(
        {
            axis: "y",
            align: "start",
            active: length > 5,
        },
        [WheelGesturesPlugin()]
    )

export interface PdpImageStyle extends CSSProperties {
    ["--carousel-space-out"]: `${number}px`
    ["--carousel-space-x"]: `${number}px`
    ["--carousel-space-y"]: `${number}px`
    ["--lg-slide-w"]: `${number}px`
    ["--lg-slide-h"]: `${number}px`
    ["--lg-slide-container-h"]: `${number}px`
    ["--lg-zoom-w"]: `${number}px`
    ["--lg-zoom-h"]: `${number}px`
    ["--lg-carousel-w"]: `${number}px`
    ["--xl-slide-w"]: `${number}px`
    ["--xl-slide-h"]: `${number}px`
    ["--xl-slide-container-h"]: `${number}px`
    ["--xl-zoom-w"]: `${number}px`
    ["--xl-zoom-h"]: `${number}px`
    ["--xl-carousel-w"]: `${number}px`
    ["--xxl-slide-w"]: `${number}px`
    ["--xxl-slide-h"]: `${number}px`
    ["--xxl-slide-container-h"]: `${number}px`
    ["--xxl-zoom-w"]: `${number}px`
    ["--xxl-zoom-h"]: `${number}px`
    ["--xxl-carousel-w"]: `${number}px`
}

export const genPdpImageStyle = (
    space: {
        out: number
        x: number
        y: number
    },
    width: { lg: number; xl: number; xxl: number },
    imagesLength: number
): PdpImageStyle => {
    const gen = (slideW: number) => {
        const slideH = (slideW / 2) * 3
        const zoomH = slideH * 5 + space.y * 4 + (imagesLength > 5 ? 0.4 * slideH : 0)
        const zoomW = (zoomH / 3) * 2
        return {
            ["slide-w"]: `${slideW}px`,
            ["slide-h"]: `${slideH}px`,
            ["slide-container-h"]: `${zoomH + space.y}px`,
            ["zoom-w"]: `${zoomW}px`,
            ["zoom-h"]: `${zoomH}px`,
            ["carousel-w"]: `${zoomW + slideW + space.out * 2 + space.x}px`,
        } as const
    }
    const lg = gen(width.lg)
    const xl = gen(width.xl)
    const xxl = gen(width.xxl)
    return {
        ["--carousel-space-x"]: `${space.x}px`,
        ["--carousel-space-y"]: `${space.y}px`,
        ["--carousel-space-out"]: `${space.out}px`,
        ["--lg-slide-w"]: lg["slide-w"],
        ["--lg-slide-h"]: lg["slide-h"],
        ["--lg-slide-container-h"]: lg["slide-container-h"],
        ["--lg-zoom-w"]: lg["zoom-w"],
        ["--lg-zoom-h"]: lg["zoom-h"],
        ["--lg-carousel-w"]: lg["carousel-w"],
        ["--xl-slide-w"]: xl["slide-w"],
        ["--xl-slide-h"]: xl["slide-h"],
        ["--xl-slide-container-h"]: xl["slide-container-h"],
        ["--xl-zoom-w"]: xl["zoom-w"],
        ["--xl-zoom-h"]: xl["zoom-h"],
        ["--xl-carousel-w"]: xl["carousel-w"],
        ["--xxl-slide-w"]: xxl["slide-w"],
        ["--xxl-slide-h"]: xxl["slide-h"],
        ["--xxl-slide-container-h"]: xxl["slide-container-h"],
        ["--xxl-zoom-w"]: xxl["zoom-w"],
        ["--xxl-zoom-h"]: xxl["zoom-h"],
        ["--xxl-carousel-w"]: xxl["carousel-w"],
    }
}
